<template>
  <div class="field-wrapper-wuk dropdown-wrapper-wuk bg-fill" ref="dropdown"
    :class="{ open, disabled, 'sword-field-label-as-placeholder': labelAsPlaceholder, '--bg-fill': fillBg }">
    <slot name="label">
      <input-label
        v-if="label"
        :label="label"
        :style-type="labelStyle"
        :info-text="labelInfo"
        :required="required && showRequiredStar"
        :disabled="disabled"
        :label-as-placeholder="labelAsPlaceholder"
        :filled="filled"
        :invalid="invalidField"
        :for="uniqueId"
      />
    </slot>
    <div class="dropdown-positioning-container">
      <div
        ref="dropdownDisplay"
        class="dropdown-display-wuk sword-boxed-input-wuk sword-input-with-icon"
        :class="{ open, disabled, ...inputStateClass, ...errorFeedbackClass}"
        :tabindex="tabindex"
        @blur.capture="handleBlur"
        @click="toggleState(true)"
      >
        <slot name="prefix"></slot>

        <base-input
          :id="uniqueId"
          v-model="inputValue"
          :required="required"
          :disabled="disabled"
          :placeholder="computedPlaceholder"
          :validation="validation"
          ref="swordDropdownInput"
          autocomplete="off"
          :readonly="!searchable"
          @input="() => inputSearch = inputValue"
          @focus="toggleState(true)"
        />

        <div class="sword-dropdown-icon-wrapper">
          <sword-icon-wuk
            class="sword-input-icon-wuk"
            :name="arrowIcon"
            :class="open ? 'svg-down' : 'svg-up'"
          />
        </div>
      </div>
      <sword-dropdown-options-wuk :show="open"
                                  class="dropdown-options-container"
                                  v-model="optionSelected"
                                  ref="dropdownOptions"
                                  :show-counters="showCounters"
                                  :options="options"
                                  :parent-search="inputSearch"
                                  :search-placeholder="searchPlaceholder"
                                  :item-align="itemAlign"
                                  :no-results-label="noResultsLabel"
                                  @change="setSelectedToInput"
                                  >
      </sword-dropdown-options-wuk>

      <mobile-options-list-wuk :show="open"
                              class="mobile-dropdown-options-container"
                              v-model="optionSelected"
                              heading="Select one"
                              :options="options"
                              ref="dropdownMobileOptions"
                              :show-counters="showCounters"
                              :searchable="searchable"
                              :search-placeholder="searchPlaceholder"
                              :item-align="itemAlign"
                              :no-results-label="noResultsLabel"
                              @closed="toggleState(false)">
      </mobile-options-list-wuk>
    </div>
    <input-footer-wuk
      v-if="!hideErrors"
      :is-invalid="invalidField"
      :validator="validation"
      :manual-error="manualError"
      :help-text="helpText"
      :success-text="successText"
    />
  </div>
</template>

<script>
import inputHandler from '@ui-kit/mixins/inputHandler';
import BaseInput from '@ui-kit/components/inputs/BaseInput.vue';
import inputValidationTriggers from '@ui-kit/mixins/inputValidationTriggers';
import mobileOptionsListWuk from '@ui-kit/components/inputs/options-lists/mobileOptionsList.vue';
import swordDropdownOptionsWuk from '@ui-kit/components/inputs/dropdowns/SwordDropdownOptions.vue';

import InputLabel from '@ui-kit/components/inputs/labels/InputLabel.vue';
import InputFooterWuk from '@ui-kit/components/inputs/footnotes/InputFooter.vue';

import SwordIconWuk from '@ui-kit/components/icons/SwordIcon.vue';

import validationClasses from '@ui-kit/mixins/inputValidationClasses';
import computeOptions from '@ui-kit/scripts/utils/options-mapper';

export default {
  name: 'swordDropdownWuk',
  mixins: [inputHandler, inputValidationTriggers, validationClasses],
  components: {
    BaseInput,
    InputLabel,
    SwordIconWuk,
    swordDropdownOptionsWuk,
    mobileOptionsListWuk,
    InputFooterWuk,
  },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    value: {
      type: [String, Number],
      default: '',
    },
    valueLabel: String, // TODO This can be deprecated
    placeholder: {
      type: String,
      default: 'Choose',
    },
    arrowIcon: {
      type: String,
      default: 'chevron-down',
    },
    state: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showCounters: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      default: 'Search',
    },
    tabindex: {
      type: Number,
      default: 0,
    },
    itemAlign: {
      type: String,
      default: 'start',
      validator: value => ['center', 'start', 'end'].includes(value),
    },
    noResultsLabel: {
      type: String,
      default: 'No results for'
    },
  },
  data() {
    return {
      // dropdownOptionsOpen: false,
      selected: null,
      open: false, // selectedx: this.value,
      searchValue: null,
      searchFocused: false,
      inputValue: '',
      inputSearch: '',
    };
  },
  computed: {
    optionSelected: {
      get() {
        return this.value;
      },
      set(value) {
        this.toggleState(false);
        this.$emit('change', value);
      },
    },
    displayValue() {
      return this.valueLabel || this.selectedOpt ? (this.selectedOpt.optSelectedLabel || this.selectedOpt.optLabel) : '';
    },
    selectedOpt() {
      return this.optionsObject.options.find(opt => opt.optValue === this.value) || null;
    },
    optionsObject() {
      return computeOptions(this.options);
    },
  },
  methods: {
    toggleState(newState) {
      if (this.disabled) {
        return;
      }

      if (!this.open && this.validation.$touch) {
        this.validation.$touch();
      }
      this.open = newState !== undefined ? newState : !this.open;
      this.inputSearch = '';
    },
    close({ relatedTarget }) {
      const nonDropdownBodyEl = !this.$refs.dropdown.contains(relatedTarget);
      if (nonDropdownBodyEl) {
        this.toggleState(false);
      }
    },
    handleBlur($event) {
      let ev = $event;
      if ($event.ev) {
        // Support for our custom event object {$el, ev}
        ev = $event.ev;
      }

      ev.preventDefault();

      if (this.disabled) return;

      if (!$event.relatedTarget || !this.$refs.dropdown.contains($event.relatedTarget)) {
        this.close($event);
        this.setDropValue();
      }
    },
    setSelectedToInput() {
      this.$nextTick(() => {
        this.inputValue = this.$t(this.displayValue);
      });
    },
    setDropValue() {
      this.$nextTick(() => {
        if (!this.inputValue) {
          this.$emit('change', '');
          this.validation.$touch();
        } else {
          this.setSelectedToInput();
        }
      });
    }
  },
  watch: {
    value(newV, old) {
      if (newV !== old) this.setSelectedToInput();
    }
  },
  mounted() {
    this.setSelectedToInput();
  }
};
</script>

<style lang="scss" scoped>
$icon-size: 68px;

  .dropdown-wrapper-wuk {
    width: 100%;

    &.open {
      .dropdown-display-wuk {
        position: relative;
        overflow: visible;
      }

      .dropdown-options-wuk {
        background: white;
        color: $dropdown-color-wuk;
        border-color: $input-border-color-wuk;
      }
    }

    .dropdown-positioning-container {
      position: relative;
    }

    .dropdown-display-wuk {

      .sword-base-input-wuk {
        color: inherit;

        &:not(:disabled):read-only {
          cursor: pointer;
        }
      }

      &:not(.filled) {
        color: $input-default-text-color-wuk;
      }

      &:not(.filled).open {
        color: $input-disabled-text-color-wuk;
      }

      &:focus {
        outline: none;
      }

      .sword-dropdown-icon-wrapper {
        flex-shrink: 0;
        padding-right: 1rem;

        svg {
          color: $context-color-1;
          transition: transform 0.3s cubic-bezier(0, 0.74, 0.67, 1.01);
        }
      }

      &.disabled {
        border-color: $input-disabled-border-color-wuk;
        background-color: $input-disabled-background-color-wuk;
        color: $input-disabled-text-color-wuk;
        cursor: not-allowed;

        &:focus,
        &:hover {
          border-color: $input-disabled-border-color-wuk;
          background-color: $input-disabled-background-color-wuk;
          color: $input-disabled-text-color-wuk;
        }

        input {
          background-color: $input-disabled-background-color-wuk;
        }

        svg {
          color: $input-disabled-text-color-wuk;
        }
      }

      &:not(.filled) {
        .display-text {
          color: $input-placeholder-text-color-wuk;
        }
      }
    }

    .display-content {
      max-width: calc(100% - #{$icon-size});
      line-height: 1.375rem;
      &::v-deep {
        & > * {
          height: initial;
          padding: 0;
        }
      }
    }

    .display-text {
      display: block;
      min-height: 1.375rem;
    }

    .dropdown-display-wuk,
    .dropdown-options-wuk {
      color: $dropdown-color-wuk;
      box-sizing: border-box;
    }

    .dropdown-display-wuk,
    .dropdown-item {
      text-align: left;
      cursor: pointer;
    }
  }

  // Only until max smartphone size
  @media screen and (max-width: break-wuk('small', 'm')) {
    .dropdown-options-container {
      display: none;
    }
  }

  // Only above smartphone size
  @media screen and (min-width: (break-wuk('small', 'm')+1)) {

    .mobile-dropdown-options-container {
      display: none;
    }
  }
</style>
